import { useCallback, useRef, useState } from "react";
import AnalysisModelAPI from "../../apis/analysis-model/analysisModelAPI.js";
import { emptyPolygon } from "../../conts/defaultValues.js";
import { getImpactErrorMessage } from "../../utils/impactErrors.js";
import { todayStamp } from "../../utils/date.js";

const MIN_LOADING_MS = 10000;
const EMPTY = { data: null, isLoading: false, error: null };

// The current analysis: polygons -> impacts -> cost -> report
const useAnalysisWorkflow = () => {
  const analysisApi = useRef(new AnalysisModelAPI()).current;
  const impactsRequest = useRef(0); // ignores late responses after a reset

  const [realPolygon, setRealPolygon] = useState(emptyPolygon);
  const [simPolygon, setSimPolygon] = useState(emptyPolygon);
  const [impacts, setImpacts] = useState(EMPTY);
  const [cost, setCost] = useState(EMPTY);
  const [report, setReport] = useState(EMPTY);

  const analysisId = impacts.data?.id;
  const moneyId = cost.data?.id;

  // clears the report and frees its PDF url
  const clearReport = () =>
    setReport((prev) => {
      if (prev.data?.url) URL.revokeObjectURL(prev.data.url);
      return EMPTY;
    });

  // ---------------------- impacts post ---------------------------
 const launchAnalysis = useCallback(
  async ({ reference, fireDate, severity }) => {
    if (!realPolygon.feature || !simPolygon.feature) {
      setImpacts((prev) => ({ ...prev, error: "Les deux polygones sont requis." }));
      return;
    }
    const requestId = ++impactsRequest.current;
    // a new analysis makes the old cost and report wrong -> clear them
    clearReport();
    setCost(EMPTY);
    setImpacts({ data: null, isLoading: true, error: null });
    try {
      // starts the 10s timer at the same time as the request
      const minDelay = new Promise((resolve) => setTimeout(resolve, MIN_LOADING_MS));

      const response = await analysisApi.postImpactsApi({
        simulatedFire: simPolygon.feature,
        realFire: realPolygon.feature,
        reference,
        fireDate,
        severity,
      });
      if (response.status !== 201) throw new Error("Unexpected status");

      await minDelay; // if the response came early, wait for the rest of the 10s
      if (requestId !== impactsRequest.current) return;

      setImpacts({ data: response.data, isLoading: false, error: null });
    } catch (err) {
      if (requestId !== impactsRequest.current) return;
      console.error("Impacts analysis error:", err);
      setImpacts({ data: null, isLoading: false, error: getImpactErrorMessage(err) });
    }
  },
  [realPolygon.feature, simPolygon.feature],
);
  // ---------------------- cost post ---------------------------
  const launchCost = useCallback(
    async ({ ressources, unitValues }) => {
      if (!analysisId) {
        setCost((prev) => ({ ...prev, error: "Aucune analyse disponible." }));
        return;
      }
      clearReport(); // a new cost makes the old report wrong
      setCost({ data: null, isLoading: true, error: null });
      try {
        const response = await analysisApi.postCostApi({ analysisId, ressources, unitValues });
        if (response.status !== 201) throw new Error("Unexpected status");
        setCost({ data: response.data, isLoading: false, error: null });
      } catch (err) {
        console.error("Cost analysis error:", err);
        setCost({ data: null, isLoading: false, error: "Impossible de calculer les coûts." });
      }
    },
    [analysisId],
  );

  // ---------------------- report generation ---------------------------
  const launchReport = useCallback(
    async (type) => {
      if (!moneyId) {
        setReport({ data: null, isLoading: false, error: "Aucun calcul de coût disponible." });
        return;
      }
      setReport((prev) => {
        if (prev.data?.url) URL.revokeObjectURL(prev.data.url);
        return { data: null, isLoading: true, error: null };
      });
      try {
        const response = await analysisApi.postReportApi({ type, moneyId });
        const blob = response.data;
        setReport({
          data: {
            type,
            blob,
            url: URL.createObjectURL(blob),
            filename: `rapport-${type}-${todayStamp()}.pdf`,
          },
          isLoading: false,
          error: null,
        });
      } catch (err) {
        console.error("Report error:", err);
        setReport({ data: null, isLoading: false, error: "Impossible de générer le rapport." });
      }
    },
    [moneyId],
  );

  // ---------------------- reset everything ---------------------------
  const resetAnalysis = useCallback(() => {
    impactsRequest.current++; // ignore a late impacts response
    clearReport();
    setCost(EMPTY);
    setImpacts(EMPTY);
    setRealPolygon(emptyPolygon);
    setSimPolygon(emptyPolygon);
  }, []);

  return {
    realPolygon,
    setRealPolygon,
    simPolygon,
    setSimPolygon,
    impacts,
    launchAnalysis,
    cost,
    launchCost,
    report,
    launchReport,
    resetAnalysis,
  };
};

export default useAnalysisWorkflow;
